import React, { useState, useRef, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Box,
  Typography,
  Paper,
  Chip,
  Avatar,
  Divider,
  Button,
  Tooltip,
  Badge,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
} from "@mui/material";
import {
  Close as CloseIcon,
  Send as SendIcon,
  Person as PersonIcon,
  Mic as MicIcon,
  VolumeUp as VolumeUpIcon,
  Translate as TranslateIcon,
  Psychology as PsychologyIcon,
  Help as HelpIcon,
  ExpandMore as ExpandMoreIcon,
  QuestionAnswer as QuestionIcon,
  Category as CategoryIcon,
} from "@mui/icons-material";
import { smartFaqEngine } from "../data/smartFaqSystem";
import { faqDatabase, categories } from "../data/faqDatabase";

const languages = {
  en: 'English',
  hi: 'हिंदी'
};

const SmartFaqChatBot = ({ open, onClose }) => {
  const [messages, setMessages] = useState([
    {
      id: 1,
      text: "👋 नमस्ते! I'm the AI Powered Smart HMIS Assistant for KS Ganga Hospital. I can help you in both English and Hindi. How can I help you today?",
      sender: "bot",
      timestamp: new Date(),
    }
  ]);
  const [inputMessage, setInputMessage] = useState("");
  const [currentLanguage, setCurrentLanguage] = useState("en");
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [isTyping, setIsTyping] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (smartFaqEngine.setLanguage) {
      smartFaqEngine.setLanguage(currentLanguage);
    }
  }, [currentLanguage]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const addMessage = (text, sender = "user", category = null) => {
    const newMessage = {
      id: Date.now(),
      text,
      sender,
      timestamp: new Date(),
      language: currentLanguage,
      category: category,
    };
    setMessages(prev => [...prev, newMessage]);
  };

  const speakText = (text) => {
    if (voiceEnabled && 'speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = currentLanguage === 'hi' ? 'hi-IN' : 'en-US';
      utterance.rate = 0.8;
      window.speechSynthesis.speak(utterance);
    }
  };

  const handleSendMessage = () => {
    if (!inputMessage.trim()) return;

    const userMessage = inputMessage.trim();
    addMessage(userMessage, "user");
    setInputMessage("");
    setShowSuggestions(false);
    
    setIsTyping(true);
    setTimeout(() => {
      setIsTyping(false);
      processUserMessage(userMessage);
    }, 1000);
  };

  const processUserMessage = (message) => {
    // Use the smart FAQ system to generate responses
    const response = smartFaqEngine.generateSmartResponse(message);
    
    let botResponse = "";
    let responseCategory = null;
    
    switch (response.type) {
      case 'greeting':
      case 'help':
      case 'goodbye':
        botResponse = response.message;
        break;
        
      case 'faq_response':
        if (response.faqs && response.faqs.length > 0) {
          const topResult = response.faqs[0];
          botResponse = `📋 **${topResult.question}**\n\n${topResult.answer}`;
          responseCategory = topResult.category;
          
          if (topResult.steps && topResult.steps.length > 0) {
            botResponse += `\n\n📝 **${currentLanguage === 'hi' ? 'चरण:' : 'Steps:'}**\n`;
            topResult.steps.forEach((step, index) => {
              botResponse += `${index + 1}. ${step}\n`;
            });
          }
          
          if (topResult.tips && topResult.tips.length > 0) {
            botResponse += `\n\n💡 **${currentLanguage === 'hi' ? 'सुझाव:' : 'Tips:'}**\n`;
            topResult.tips.forEach(tip => {
              botResponse += `• ${tip}\n`;
            });
          }
        } else {
          botResponse = response.message;
        }
        break;
        
      case 'no_match':
      default:
        botResponse = currentLanguage === 'hi' 
          ? `🤔 मुझे "${message}" का विशिष्ट उत्तर नहीं मिला। नीचे categories देखें।\n\n📞 तत्काल सहायता के लिए संपर्क करें:\nbeenaIT Solutions\nEmail: contact-us@beenait.com\nPhone: +91 77177 32269`
          : `🤔 I couldn't find a specific answer for "${message}". Try the categories below.\n\n📞 For immediate help, contact:\nbeenaIT Solutions\nEmail: contact-us@beenait.com\nPhone: +91 77177 32269`;
        setShowSuggestions(true);
        break;
    }
    
    addMessage(botResponse, "bot", responseCategory);
    speakText(botResponse.replace(/\*\*/g, '').replace(/[📋📝💡•🤔📞]/g, ''));
    
    // Log interaction for analytics
    if (smartFaqEngine.logInteraction) {
      smartFaqEngine.logInteraction(message, response);
    }
  };

  const formatMessage = (text) => {
    const baseTextStyle = {
      wordBreak: 'break-word',
      overflowWrap: 'break-word',
      whiteSpace: 'pre-wrap'
    };

    const renderTextWithBold = (text) => {
      // Split text by ** markers and alternate between normal and bold
      const parts = text.split(/(\*\*.*?\*\*)/g);
      
      return parts.map((part, index) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          // Bold text
          return (
            <Box component="span" key={index} sx={{ fontWeight: 'bold', color: '#1976d2' }}>
              {part.replace(/\*\*/g, '')}
            </Box>
          );
        }
        // Normal text
        return part;
      });
    };

    return text.split('\n').map((line, index) => {
      if (line.match(/^[0-9]+\./)) {
        return (
          <Typography key={index} variant="body2" sx={{ 
            ml: 1, 
            mt: 0.5,
            ...baseTextStyle
          }}>
            {renderTextWithBold(line)}
          </Typography>
        );
      }
      if (line.startsWith('•') || line.startsWith('📞')) {
        return (
          <Typography key={index} variant="body2" sx={{ 
            ml: 1, 
            mt: 0.5,
            ...baseTextStyle
          }}>
            {renderTextWithBold(line)}
          </Typography>
        );
      }
      return line.trim() ? (
        <Typography key={index} variant="body2" sx={{ 
          mt: index > 0 ? 0.5 : 0,
          ...baseTextStyle
        }}>
          {renderTextWithBold(line)}
        </Typography>
      ) : null;
    });
  };

  const handleLanguageSwitch = (newLanguage) => {
    setCurrentLanguage(newLanguage);
    if (smartFaqEngine.setLanguage) {
      smartFaqEngine.setLanguage(newLanguage);
    }
    
    const switchMessage = newLanguage === 'hi' 
      ? "भाषा हिंदी में बदल दी गई।"
      : "Language switched to English.";
    
    addMessage(switchMessage, "bot");
    speakText(switchMessage);
  };

  const getQuickActions = () => {
    return currentLanguage === 'hi' ? [
      "मरीज़ का पंजीकरण कैसे करें?",
      "अपॉइंटमेंट कैसे बुक करें?",
      "बिल कैसे बनाएं?"
    ] : [
      "How to register a patient?",
      "How to book appointment?",
      "How to generate bill?"
    ];
  };

  const handleQuickAction = (action) => {
    setInputMessage(action);
    setTimeout(() => handleSendMessage(), 100);
  };

  const getCategories = () => {
    return [...new Set(faqDatabase.map(faq => faq.category))];
  };

  const getFaqsByCategory = (category) => {
    return faqDatabase.filter(faq => faq.category === category);
  };

  const handleFaqClick = (faq) => {
    let response = `**${faq.question}**\n\n${faq.answer}`;
    if (faq.steps && faq.steps.length > 0) {
      response += `\n\n**Steps:**\n`;
      faq.steps.forEach((step, index) => {
        response += `${index + 1}. ${step}\n`;
      });
    }
    if (faq.tips && faq.tips.length > 0) {
      response += `\n\n**Tips:**\n`;
      faq.tips.forEach(tip => {
        response += `• ${tip}\n`;
      });
    }
    addMessage(response, "bot", faq.category);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          height: '85vh',
          maxHeight: '700px',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      <DialogTitle sx={{ 
        p: 2, 
        backgroundColor: 'linear-gradient(135deg, #1976d2, #42a5f5)',
        background: 'linear-gradient(135deg, #1976d2, #42a5f5)',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <Badge badgeContent="AI" color="secondary">
            <PsychologyIcon sx={{ fontSize: 30 }} />
          </Badge>
          <Box>
            <Typography variant="h6">Smart HMIS Assistant</Typography>
            <Typography variant="caption" sx={{ opacity: 0.9 }}>
              Self-Learning • Multilingual • Voice Enabled
            </Typography>
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<TranslateIcon />}
            onClick={() => handleLanguageSwitch(currentLanguage === 'hi' ? 'en' : 'hi')}
            sx={{ 
              color: 'white', 
              borderColor: 'white',
              minWidth: '80px',
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
            }}
          >
            {currentLanguage === 'hi' ? 'English' : 'हिंदी'}
          </Button>
          <Tooltip title={voiceEnabled ? 'Voice Off' : 'Voice On'}>
            <IconButton 
              onClick={() => setVoiceEnabled(!voiceEnabled)}
              sx={{ color: voiceEnabled ? '#ffeb3b' : 'white' }}
            >
              <VolumeUpIcon />
            </IconButton>
          </Tooltip>
          <IconButton onClick={onClose} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0, display: 'flex', height: '100%' }}>
        {/* Chat Area */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ flex: 1, overflowY: 'auto', p: 2, backgroundColor: '#f8f9fa' }}>
            {messages.map((message) => (
              <Box
                key={message.id}
                sx={{
                  display: 'flex',
                  justifyContent: message.sender === 'user' ? 'flex-end' : 'flex-start',
                  mb: 2,
                  alignItems: 'flex-start',
                }}
              >
                {message.sender === 'bot' && (
                  <Avatar sx={{ 
                    background: 'linear-gradient(135deg, #1976d2, #42a5f5)', 
                    mr: 1, 
                    width: 36, 
                    height: 36 
                  }}>
                    <PsychologyIcon fontSize="small" />
                  </Avatar>
                )}
                
                <Paper
                  elevation={message.sender === 'user' ? 3 : 1}
                  sx={{
                    p: 2,
                    maxWidth: '70%',
                    background: message.sender === 'user' 
                      ? 'linear-gradient(135deg, #1976d2, #42a5f5)'
                      : 'white',
                    color: message.sender === 'user' ? 'white' : 'text.primary',
                    borderRadius: message.sender === 'user' ? '20px 20px 4px 20px' : '20px 20px 20px 4px',
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word',
                    minWidth: 0, // Allow flexbox to shrink below content size
                  }}
                >
                  {formatMessage(message.text)}
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      display: 'block', 
                      mt: 1, 
                      opacity: 0.7,
                      fontSize: '0.7rem'
                    }}
                  >
                    {message.timestamp.toLocaleTimeString()}
                    {message.language && (
                      <Chip 
                        label={languages[message.language]} 
                        size="small" 
                        sx={{ ml: 1, height: 16, fontSize: '0.6rem' }}
                      />
                    )}
                    {message.category && (
                      <Chip 
                        label={categories.find(c => c.id === message.category)?.name || message.category}
                        size="small" 
                        sx={{ ml: 1, height: 16, fontSize: '0.6rem' }}
                        color="primary"
                      />
                    )}
                  </Typography>
                </Paper>

                {message.sender === 'user' && (
                  <Avatar sx={{ backgroundColor: '#666', ml: 1, width: 36, height: 36 }}>
                    <PersonIcon fontSize="small" />
                  </Avatar>
                )}
              </Box>
            ))}

            {isTyping && (
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar sx={{ 
                  background: 'linear-gradient(135deg, #1976d2, #42a5f5)', 
                  mr: 1, 
                  width: 36, 
                  height: 36 
                }}>
                  <PsychologyIcon fontSize="small" />
                </Avatar>
                <Paper elevation={1} sx={{ p: 2, backgroundColor: 'white' }}>
                  <Typography variant="body2" sx={{ fontStyle: 'italic', color: '#666' }}>
                    {currentLanguage === 'hi' ? 'असिस्टेंट सोच रहा है...' : 'Smart Assistant is thinking...'}
                  </Typography>
                </Paper>
              </Box>
            )}

            {showSuggestions && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, color: '#666', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <HelpIcon fontSize="small" />
                  {currentLanguage === 'hi' ? 'त्वरित प्रश्न:' : 'Quick Questions:'}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {getQuickActions().map((action, index) => (
                    <Chip
                      key={index}
                      label={action}
                      onClick={() => handleQuickAction(action)}
                      sx={{ 
                        cursor: 'pointer',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #1976d2, #42a5f5)',
                          color: 'white'
                        }
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}

            <div ref={messagesEndRef} />
          </Box>

          <Divider />

          <Box sx={{ p: 2, backgroundColor: 'white' }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
            <TextField
              fullWidth
              multiline
              maxRows={4}
              placeholder={currentLanguage === 'hi' ? "HMIS के बारे में पूछें..." : "Ask about HMIS features..."}
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && !e.shiftKey && handleSendMessage()}
              variant="outlined"
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  alignItems: 'flex-start'
                }
              }}
            />
            <IconButton
              onClick={handleSendMessage}
              disabled={!inputMessage.trim()}
              sx={{ backgroundColor: '#1976d2', color: 'white' }}
            >
              <SendIcon />
            </IconButton>
          </Box>
        </Box>
        </Box>

        <Divider orientation="vertical" flexItem />

        {/* Category Sidebar */}
        <Box sx={{ width: 320, p: 2, backgroundColor: '#fafafa', overflowY: 'auto' }}>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <CategoryIcon color="primary" />
            {currentLanguage === 'hi' ? 'FAQ श्रेणियां' : 'FAQ Categories'}
          </Typography>

          {getCategories().map((category) => (
            <Accordion 
              key={category} 
              expanded={selectedCategory === category}
              onChange={() => setSelectedCategory(selectedCategory === category ? null : category)}
              sx={{ mb: 1, boxShadow: 1 }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2" sx={{ textTransform: 'capitalize' }}>
                  {categories.find(c => c.id === category)?.name || category.replace('_', ' ')}
                </Typography>
                <Chip 
                  label={getFaqsByCategory(category).length} 
                  size="small" 
                  sx={{ ml: 'auto', mr: 1 }}
                  color="primary"
                />
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                <List dense>
                  {getFaqsByCategory(category).map((faq) => (
                    <ListItem key={faq.id} disablePadding>
                      <ListItemButton 
                        onClick={() => handleFaqClick(faq)}
                        sx={{
                          '&:hover': {
                            backgroundColor: '#e3f2fd'
                          }
                        }}
                      >
                        <ListItemText 
                          primary={faq.question}
                          primaryTypographyProps={{ 
                            variant: 'body2',
                            sx: { fontSize: '0.85rem' }
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          ))}

          <Box sx={{ mt: 3, p: 2, backgroundColor: '#e3f2fd', borderRadius: 2 }}>
            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <QuestionIcon fontSize="small" />
              {currentLanguage === 'hi' ? 'सहायता की आवश्यकता?' : 'Need Help?'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {currentLanguage === 'hi' 
                ? 'ऊपर categories browse करें या सीधे सवाल पूछें।' 
                : 'Browse categories above or ask questions directly.'
              }
            </Typography>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default SmartFaqChatBot;
